import { useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
} from '@fluentui/react-components';
import { CopyRegular, OpenRegular } from '@fluentui/react-icons';
import { Link, Subtitle2 } from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { STATUS_COLOR, STATUS_LABEL } from '../components/approvalStatus';
import type { ApprovalRequestDto, MyIssueDto } from '../api/types';

export function MyRequestsPage() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.getMyApprovals(), [api]);
  const issues = useAsync(() => api.getMyIssues(), [api]);

  return (
    <div>
      <PageHeader title="Meine Anfragen" description="Deine Freigabe-Anträge und gemeldeten Feature Requests / Bugs samt Status." />
      {loading ? <Spinner label="Lade…" /> : null}
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      ) : null}
      {data && data.length === 0 ? <Body1>Noch keine Anträge.</Body1> : null}
      {data && data.length > 0 ? (
        <Table aria-label="Meine Anfragen" size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Erstellt</TableHeaderCell>
              <TableHeaderCell>Aktion</TableHeaderCell>
              <TableHeaderCell>Ticket</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Ergebnis</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{new Date(r.createdUtc).toLocaleString('de-DE')}</TableCell>
                <TableCell>
                  <span className="pii">{r.targetSummary}</span>
                </TableCell>
                <TableCell>{r.ticketNumber ?? '—'}</TableCell>
                <TableCell>
                  <Badge appearance="tint" color={STATUS_COLOR[r.status]}>
                    {STATUS_LABEL[r.status]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <ResultCell entry={r} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      <div style={{ marginTop: tokens.spacingVerticalXXL }}>
        <Subtitle2 block style={{ marginBottom: tokens.spacingVerticalS }}>
          Feature Requests &amp; Bugs
        </Subtitle2>
        {issues.loading ? <Spinner size="tiny" label="Lade…" /> : null}
        {issues.error ? (
          <MessageBar intent="error">
            <MessageBarBody>{issues.error.message}</MessageBarBody>
          </MessageBar>
        ) : null}
        {issues.data && issues.data.length === 0 ? (
          <Body1>Noch keine gemeldeten Feature Requests oder Bugs.</Body1>
        ) : null}
        {issues.data && issues.data.length > 0 ? (
          <Table aria-label="Meine Issues" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Nr.</TableHeaderCell>
                <TableHeaderCell>Titel</TableHeaderCell>
                <TableHeaderCell>Typ</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Letzte Aktivität</TableHeaderCell>
                <TableHeaderCell>GitHub</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {issues.data.map((i) => (
                <TableRow key={i.number}>
                  <TableCell>#{i.number}</TableCell>
                  <TableCell>{i.title}</TableCell>
                  <TableCell>{i.type === 'bug' ? 'Bug' : 'Feature'}</TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={i.state === 'closed' ? 'success' : 'informative'}>
                      {i.state === 'closed' ? 'Erledigt' : i.state === 'open' ? 'Offen' : i.state}
                    </Badge>
                    {i.comments > 0 ? (
                      <Caption1 style={{ marginInlineStart: 6 }}>{i.comments} Kommentar(e)</Caption1>
                    ) : null}
                  </TableCell>
                  <TableCell>{new Date(i.updatedUtc).toLocaleString('de-DE')}</TableCell>
                  <TableCell>
                    <Link href={i.htmlUrl} target="_blank" rel="noreferrer">
                      öffnen <OpenRegular />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </div>
    </div>
  );
}

function ResultCell({ entry }: { entry: ApprovalRequestDto }) {
  const [copied, setCopied] = useState(false);

  if (entry.status === 'Executed' && entry.temporaryPassword) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Caption1>{entry.resultMessage}</Caption1>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
          <Text font="monospace" className="pii">
            {entry.temporaryPassword}
          </Text>
          <Button
            size="small"
            appearance="subtle"
            icon={<CopyRegular />}
            onClick={() => {
              navigator.clipboard
                .writeText(entry.temporaryPassword ?? '')
                .then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                })
                .catch(() => undefined);
            }}
          >
            {copied ? 'Kopiert' : 'Kopieren'}
          </Button>
        </div>
      </div>
    );
  }

  if (entry.resultMessage) return <Caption1>{entry.resultMessage}</Caption1>;
  if (entry.decisionComment) return <Caption1>„{entry.decisionComment}"</Caption1>;
  return <span>—</span>;
}
